import { NewsletterSubscriber } from "modules/newsletters/components/NewsletterSubscriber"
import { useNewsletterGroups } from "modules/newsletters/hooks/useNewsletterGroups"
import { useMemo } from "react"
import { Group, GroupItem } from "common/types/graphql"

/**
 * Class that assigns a rank based on the position of newsletters as they are seen by the user.
 * Promoted newsletter will always be rank 1, followed by all other newsletters sequentially.
 */
class NewsletterRankMap {
  private rankMap: Map<string, number>

  constructor(groups: Group[], promotedNewsletter: GroupItem | undefined) {
    this.rankMap = new Map<string, number>()
    let currentRank = 1

    // Promoted newsletter will always be rank 1, in case they exist.
    if (promotedNewsletter) {
      this.rankMap.set(promotedNewsletter.id, currentRank)
      currentRank++
    }

    // Add all cards across all groups sequentially
    groups.forEach(({ groupItems }) => {
      groupItems.forEach((groupItem) => {
        if (groupItem.id === promotedNewsletter?.id) return

        this.rankMap.set(groupItem.id, currentRank)
        currentRank++
      })
    })
  }

  /**
   * Gets the newsletter rank, defaulting to 0 if not found.
   */
  get(id: string): number {
    return this.rankMap.get(id) ?? 0
  }
}

/**
 * Component that will render newsletter groups as a hero and a list of newsletter cards.
 * This component contains the logic to pluck out the promoted newsletter from the given list of groups,
 * rendering on as the hero of the first group and the rest as newsletter cards.
 */
export const Groups = () => {
  const { groups, promotedNewsletter } = useNewsletterGroups()

  const newsletterRankMap = useMemo(
    () => new NewsletterRankMap(groups, promotedNewsletter),
    [groups, promotedNewsletter],
  )

  if (!groups.length) {
    return null
  }

  return (
    <main>
      {groups.map(({ name, groupItems }, index) => {
        const isFirstGroup = index === 0
        const regularNewsletters = groupItems.filter(
          (groupItem) => groupItem.id !== promotedNewsletter?.id,
        )

        return (
          <section key={name}>
            <h3 className="font-sans-heading mt-16 mb-3 text-lg font-bold">{name}</h3>

            {isFirstGroup && promotedNewsletter && (
              <NewsletterSubscriber
                type="hero"
                groupItem={promotedNewsletter}
                rank={newsletterRankMap.get(promotedNewsletter.id)}
              />
            )}

            <div className="my-5 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-8">
              {regularNewsletters.map((groupItem) => (
                <NewsletterSubscriber
                  key={groupItem.id}
                  type="card"
                  groupItem={groupItem}
                  rank={newsletterRankMap.get(groupItem.id)}
                />
              ))}
            </div>
          </section>
        )
      })}
    </main>
  )
}
